import { PriceBreakdown } from '@/components/PriceBreakdown/PriceBreakdown';
import { CouponPanel } from './CouponPanel';
import { Thumb } from '@/components/Thumb/Thumb';
import { formatPrice } from '@/utils/format';
import { lineBreakdown } from '@/utils/pricing';
import styles from './OrderSummary.module.css';

export function OrderSummary({
  items,
  totals,
  currency,
  requiresDelivery = true,
  coupon,
  // False for a language added to a book already owned: no code applies to one.
  allowCoupon = true,
  isSignedIn = false,
  onCouponInput,
  onApplyCouponCode,
  onSelectCoupon,
  onRemoveCoupon,
  error,
  payLabel,
  submitting,
  status,
  onSubmit,
}) {
  return (
    <aside className={styles.summary}>
      <h2 className={styles.title}>Order summary</h2>

      <ul className={styles.items}>
        {items.map((item) => (
          <li key={item.key ?? item.id} className={styles.item}>
            <Thumb
              letter={item.letter}
              tint={item.tint}
              deep={item.deep}
              src={item.image_url}
              size="xs"
            />
            <div className={styles.itemName}>
              {item.name}
              <PriceBreakdown item={item} currency={currency} />
              <div className={styles.itemQty}>
                {item.kind === 'renewal'
                  ? 'Renewal · one more term'
                  : item.kind === 'add_languages'
                    ? 'Added language · rest of your term'
                    : `× ${item.qty}`}
              </div>
            </div>
            <div className={styles.itemTotal}>
              {formatPrice(lineBreakdown(item).lineTotal, item.currency ?? currency)}
            </div>
          </li>
        ))}
      </ul>

      {allowCoupon && (
        <CouponPanel
          coupon={coupon}
          currency={currency}
          isSignedIn={isSignedIn}
          onInput={onCouponInput}
          onApplyCode={onApplyCouponCode}
          onSelectOffer={onSelectCoupon}
          onRemove={onRemoveCoupon}
        />
      )}

      <div className={styles.lines}>
        <div className={styles.line}>
          <span className={styles.lineLabel}>Subtotal</span>
          <span>{formatPrice(totals.subtotal, currency)}</span>
        </div>

        {/* Nothing is shipped and nothing is charged for it — but a digital
            buyer still wants to know when they get the book. */}
        {!requiresDelivery && (
          <div className={styles.line}>
            <span className={styles.lineLabel}>Delivery</span>
            <span>Instant · in playTUB</span>
          </div>
        )}

        {totals.discount > 0 && (
          <div className={`${styles.line} ${styles.discount}`}>
            <span>{coupon?.applied?.coupon_code ?? 'Discount'}</span>
            <span>−{formatPrice(totals.discount, currency)}</span>
          </div>
        )}

        <div className={styles.total}>
          <span>Total</span>
          <span>{formatPrice(totals.total, currency)}</span>
        </div>
      </div>

      {error && (
        <div className={styles.error} role="alert">
          {error}
        </div>
      )}

      {/* What the checkout is doing right now. Payment has several waits in it —
          creating the order, loading Razorpay, verifying afterwards — and a
          button that just says "…" for all of them tells nobody anything. */}
      {submitting && status && (
        <div className={styles.status} role="status">
          {status}
        </div>
      )}

      <button type="submit" className={styles.payBtn} onClick={onSubmit} disabled={submitting}>
        {submitting ? status || 'Working…' : payLabel}
      </button>

      {/* True whichever mode the gateway is in, so it says the same thing in
          both — a line that changes with the environment is a line that
          eventually says the wrong one in front of a customer. Whether this is
          a real charge is shown where it belongs, on the payment section. */}
      <div className={styles.disclaimer}>
        Payments are processed securely by Razorpay. Your card details never reach TUBMarket.
      </div>
    </aside>
  );
}
